/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { use, useMemo, type ReactElement } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Content } from '@builder.io/sdk-react';
import { GLOBAL_MODEL } from '@/lib/builder/placements';
import { BUILDER_COMPONENTS } from '@/lib/builder/registry';
import {
    hasEffectiveTargeting,
    normaliseUrlPath,
    selectTargetedEntry,
    type TargetedBuilderContent,
} from '@/lib/builder/targeting';

/**
 * The newsletter band above the footer (SCO-FOO-07). Hidden unless an entry targets this URL,
 * which is the requirement: merchandisers attach it to the pages they want it on.
 *
 * Targeting is matched here rather than by the content API because `_app`'s loader runs once
 * per document load — a server-matched entry would freeze on the landing URL. `useLocation` is
 * a context read, so this re-runs on every client-side navigation. Authoring happens on
 * `/preview/newsletter`, so no `<Content>` is mounted here for an unmatched entry.
 */
export default function NewsletterBand({
    entries,
    apiKey,
}: Readonly<{ entries: Promise<TargetedBuilderContent[]>; apiKey: string }>): ReactElement | null {
    const all = use(entries);
    const { pathname } = useLocation();
    const { t } = useTranslation('footer');

    // Placement contract, not SDK semantics: an untargeted entry matches every URL and, being the
    // oldest, would shadow every targeted one after it. Skipping it fails to hidden, the documented
    // default. Enforced here rather than in `selectTargetedEntry`, which stays general-purpose.
    const targeted = useMemo(() => {
        const kept = all.filter(hasEffectiveTargeting);
        if (import.meta.env.DEV && kept.length < all.length) {
            // oxlint-disable-next-line no-console
            console.warn(
                '[Newsletter] Ignoring untargeted entries — an entry with no targeting rules would show on every page.',
                all.filter((e) => !hasEffectiveTargeting(e)).map((e) => e.name ?? e.id)
            );
        }
        return kept;
    }, [all]);

    const entry = useMemo(
        () => selectTargetedEntry(targeted, { urlPath: normaliseUrlPath(pathname) }),
        [targeted, pathname]
    );

    if (!apiKey || !entry) return null;

    // The wrapper is the whole of our layout: no width, padding or colour, so the authored blocks
    // own the band. It stays because the placement sits between `</main>` and `<footer>`, which
    // would otherwise leave the content in no landmark at all.
    // `key`: without it the SDK merges the incoming entry into the previous one's context, so
    // swapping bands on a navigation leaks the old entry's cssCode and breakpoints.
    return (
        <section aria-label={t('newsletterRegionLabel')} data-sco-newsletter>
            <Content
                key={entry.id}
                model={GLOBAL_MODEL}
                content={entry}
                apiKey={apiKey}
                customComponents={BUILDER_COMPONENTS}
            />
        </section>
    );
}
